// ===========================================
// Newsletter Analytics Page
// ===========================================
// View detailed analytics for a sent newsletter

import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Typography,
  Alert,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as EmailIcon,
  Visibility as OpenIcon,
  TouchApp as ClickIcon,
  Error as BounceIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useNewsletter,
  useNewsletterStats,
  useDetailedAnalytics,
} from '../../hooks/useNewsletter';

// ===========================================
// STAT CARD COMPONENT
// ===========================================

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

function StatCard({ title, value, subtitle, icon, color = 'primary.main' }: StatCardProps) {
  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function NewsletterAnalyticsPage() {
  const { id, schoolSlug } = useParams<{ id: string; schoolSlug: string }>();

  const { data: newsletter, isLoading: newsletterLoading } = useNewsletter(id || '');
  const { data: stats, isLoading: statsLoading } = useNewsletterStats(id || '');
  const { data: detailed, isLoading: detailedLoading } = useDetailedAnalytics(id || '');

  const isLoading = newsletterLoading || statsLoading || detailedLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!newsletter || !stats) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load newsletter analytics
        </Alert>
        <Button
          startIcon={<BackIcon />}
          component={Link}
          to={`/${schoolSlug}/admin/newsletters`}
          sx={{ mt: 2 }}
        >
          Back to Newsletters
        </Button>
      </Box>
    );
  }

  const openRate = stats.sent > 0 ? ((stats.opened / stats.sent) * 100).toFixed(1) : '0';
  const clickRate = stats.opened > 0 ? ((stats.clicked / stats.opened) * 100).toFixed(1) : '0';
  const bounceRate = stats.sent > 0 ? ((stats.bounced / stats.sent) * 100).toFixed(1) : '0';

  return (
    <Box>
      <PageHeader
        title="Newsletter Analytics"
        subtitle={newsletter.title}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<BackIcon />}
              component={Link}
              to={`/${schoolSlug}/admin/newsletters`}
            >
              Back to List
            </Button>
            <Button
              variant="outlined"
              component={Link}
              to={`/${schoolSlug}/admin/newsletters/${id}`}
            >
              Edit Newsletter
            </Button>
          </Stack>
        }
      />

      {/* Key metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Sent"
            value={stats.sent}
            subtitle={`of ${stats.totalRecipients} recipients`}
            icon={<EmailIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Opened"
            value={stats.opened}
            subtitle={`${openRate}% open rate`}
            icon={<OpenIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clicked"
            value={stats.clicked}
            subtitle={`${clickRate}% click rate`}
            icon={<ClickIcon />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Bounced"
            value={stats.bounced}
            subtitle={`${bounceRate}% bounce rate`}
            icon={<BounceIcon />}
            color="error.main"
          />
        </Grid>
      </Grid>

      {/* Performance bars */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Delivery Performance
        </Typography>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">Delivered</Typography>
              <Typography variant="body2">
                {stats.delivered} / {stats.sent} ({stats.sent > 0 ? ((stats.delivered / stats.sent) * 100).toFixed(0) : 0}%)
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={stats.sent > 0 ? (stats.delivered / stats.sent) * 100 : 0}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">Opened</Typography>
              <Typography variant="body2">
                {stats.opened} / {stats.delivered} ({openRate}%)
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Number(openRate)}
              color="success"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">Clicked</Typography>
              <Typography variant="body2">
                {stats.clicked} / {stats.opened} ({clickRate}%)
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Number(clickRate)}
              color="info"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* Top clicked URLs */}
      {detailed && detailed.topClickedUrls.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top Clicked Links
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                  <TableCell align="right">% of Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailed.topClickedUrls.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <MuiLink
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          maxWidth: 400,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.url}
                      </MuiLink>
                    </TableCell>
                    <TableCell align="right">{item.clicks}</TableCell>
                    <TableCell align="right">
                      {stats.clicked > 0
                        ? ((item.clicks / stats.clicked) * 100).toFixed(1)
                        : 0}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Newsletter info */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Newsletter Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Status:
                </Typography>
                <Typography variant="body2">{newsletter.status}</Typography>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Visibility:
                </Typography>
                <Typography variant="body2">{newsletter.visibility}</Typography>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Author:
                </Typography>
                <Typography variant="body2">
                  {newsletter.author.firstName} {newsletter.author.lastName}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Created:
                </Typography>
                <Typography variant="body2">
                  {new Date(newsletter.createdAt).toLocaleString()}
                </Typography>
              </Stack>
              {newsletter.sentAt && (
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Sent:
                  </Typography>
                  <Typography variant="body2">
                    {new Date(newsletter.sentAt).toLocaleString()}
                  </Typography>
                </Stack>
              )}
              {newsletter.publishedAt && (
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Published:
                  </Typography>
                  <Typography variant="body2">
                    {new Date(newsletter.publishedAt).toLocaleString()}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
